import { cva, type VariantProps } from "class-variance-authority";

import { FooterProps } from "./types";

import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/LoadingSpinner";
import { cn } from "@/lib/utils";

const footerStyles = {
  container: cva("flex gap-2", {
    variants: {
      size: {
        desktop: "w-full phone:w-full phone:max-w-full phone:flex-col-reverse",
        dialog: "w-full justify-end",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  button: cva("w-full", {
    variants: {
      size: {
        desktop: "h-[44px] max-w-[135px] text-[16px] phone:w-full phone:max-w-full",
        dialog: "h-[38px] max-w-[110px] text-[14px]",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
};

export type FooterStyleProps = VariantProps<typeof footerStyles.container>;

export const Footer = ({
  isBackButtonVisible = true,
  onBack,
  onNext,
  onSkip,
  isSkippable = false,
  isBackDisabled = false,
  isNextDisabled = false,
  isNextButtonLoading = false,
  isSubmit = false,
  size = "desktop",
  isPreview = false,
  onExit,
}: FooterProps) => {
  const onNextClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (!isSubmit) {
      event.preventDefault();
      event.stopPropagation();
      onNext();
    }
  };

  const getNextButtonContent = () => {
    if (isNextButtonLoading) {
      return <LoadingSpinner size={size === "desktop" ? "4" : "3"} />;
    }

    if (isSkippable) {
      return "Skip";
    }

    if (isSubmit) {
      return "Finish";
    }

    return "Continue";
  };

  return (
    <div className={footerStyles.container({ size })}>
      {isBackButtonVisible && (
        <Button
          size="lg"
          className={footerStyles.button({ size })}
          variant="outline"
          onClick={onBack}
          type="button"
          disabled={isBackDisabled}
        >
          Back
        </Button>
      )}
      {!isPreview ? (
        <Button
          size="lg"
          className={cn(
            footerStyles.button({ size }),
            isNextButtonLoading && "flex items-center bg-gray-200",
          )}
          type={isSubmit && !isSkippable ? "submit" : "button"}
          onClick={isSkippable ? onSkip : onNextClick}
          disabled={isNextDisabled || isNextButtonLoading}
        >
          {getNextButtonContent()}
        </Button>
      ) : (
        <Button
          size="lg"
          className={cn(
            footerStyles.button({ size }),
            isNextButtonLoading && "flex items-center bg-gray-200",
          )}
          type="button"
          onClick={isSubmit ? onExit : onNextClick}
        >
          {isSubmit ? "Close" : "Continue"}
        </Button>
      )}
    </div>
  );
};
